import React, { useCallback } from 'react';

import ActionCreator from '@components/ActionCreator/ActionCreator';
import BaseModal from '@components/Overlays/Modals/BaseModal/BaseModal';
import { Details, ThreeDSDetails } from '@hooks/useActionCreator';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';
import { useRetryState } from '@providers/RetryDataProvider';
import { useThunkDispatch } from '@providers/ThunkDispatchProvider';
import { processPaymentAuthorisation } from '@services/retryPaymentService';
import { convertRunningEnvToAdyenEnv } from '@utils/utils';

const AdyenCreditCardAuthActionModal: React.FC = () => {
  const {
    config: {
      env,
      adyen: { clientKey },
    },
  } = useConfig();
  const {
    retryState: { initiatePaymentData, customerId },
    processPaymentAuthorisationSuccess,
  } = useRetryState();
  const { hideOverlay } = useOverlays();
  const { locale, shopBundle, csrfToken } = useAppContext();
  const { thunkDispatch } = useThunkDispatch();
  const { t } = useTranslation();

  const overlayId = OverlayIds.ADYEN_CREDIT_CARD_AUTH;

  const threeDSFailed = useCallback(() => {
    processPaymentAuthorisationSuccess({
      retryPaymentStatus: 'UNSUCCESSFUL',
      resultCode: 'REFUSED',
    });
  }, [processPaymentAuthorisationSuccess]);

  const actionTimedOutHandler = useCallback(() => {
    threeDSFailed();
    hideOverlay(overlayId);
  }, [hideOverlay, overlayId]);

  const handlePaymentDetails = useCallback(
    (details: Details) => {
      thunkDispatch(
        processPaymentAuthorisation({
          customerId: String(customerId),
          paymentId: initiatePaymentData?.paymentId,
          paymentMethod: 'CREDIT_CARD',
          threeDSResult: (details as ThreeDSDetails).threeDSResult,
        }),
      );
    },
    [customerId, initiatePaymentData?.paymentId],
  );

  return initiatePaymentData?.actionObject ? (
    <BaseModal
      modalId={overlayId}
      heading={t('mzp.order.detail.retry.payment.threeds.info')}
      headingId={'adyen-credit-card-auth-modal'}
      closeBtnAriaLabel={'Close'}
      onClose={threeDSFailed}
      dataZta={'adyen-credit-card-auth-modal'}
      size={'md'}>
      <ActionCreator
        paymentAction={initiatePaymentData.actionObject}
        locale={locale}
        shopBundle={shopBundle}
        actionTimedOutHandler={actionTimedOutHandler}
        csrfToken={csrfToken}
        paymentId={initiatePaymentData.paymentId}
        paymentMethod={'AK'}
        sendPaymentDetailsHandler={handlePaymentDetails}
        adyenEnvironment={convertRunningEnvToAdyenEnv(env)}
        clientKey={clientKey}
      />
    </BaseModal>
  ) : null;
};

export default AdyenCreditCardAuthActionModal;
